// shared/src/resolvers/chain-id.ts
// 2026-06-09 — translation lives in CODE, not in config the operator must decode.
//
// The Account Holdings feature stores its tracked EVM chains in the
// `ACCOUNT_CHAINS` runtime_param. Historically that param held raw numeric
// chain IDs (`["1","137","42161","10","8453"]`) — but a numeric chain ID is
// opaque in the Params tab ("I can't tell what 42161 is"). This resolver lets
// the operator write human abbreviations (`["ETH","POLY","ARB","OPT","BASE"]`)
// and the code maps them to the canonical numeric chain ID every downstream
// consumer (Alchemy `chains:`, Revert, projectors) already expects.
//
// Per [[feedback_translation_in_code_not_llm]] — the resolver sits at the
// account-worker boundary; nothing downstream changes shape (still numeric IDs).
//
// Fail loud per [[feedback_no_fallbacks]]: an unknown token returns `null`; the
// caller surfaces the rejected value + accepted list. NEVER falls back to a
// default chain (a silent default would scan the wrong chain).
//
// Adding a chain: add its CANONICAL abbrev → numeric mapping below (+ any extra
// aliases). The numeric form is always accepted as passthrough.

/** CANONICAL display abbreviation → numeric EVM chain ID (string form). */
const ABBREV_TO_CHAIN_ID: Readonly<Record<string, string>> = {
  ETH:  '1',      // Ethereum mainnet
  POLY: '137',    // Polygon PoS
  ARB:  '42161',  // Arbitrum One
  OPT:  '10',     // OP Mainnet (Optimism)
  BASE: '8453',   // Base
};

/** Extra accepted aliases (full names / common synonyms) → numeric chain ID.
 *  Kept separate from the canonical map so listKnownChainAbbrevs() surfaces only
 *  the short forms the operator should store. */
const ALIAS_TO_CHAIN_ID: Readonly<Record<string, string>> = {
  ETHEREUM:  '1',
  MAINNET:   '1',
  MATIC:     '137',
  POLYGON:   '137',
  ARBITRUM:  '42161',
  OPTIMISM:  '10',
};

/** numeric chain ID → canonical abbrev (for the passthrough / known-id check). */
const CHAIN_ID_TO_ABBREV: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = {};
  for (const [abbrev, id] of Object.entries(ABBREV_TO_CHAIN_ID)) out[id] = abbrev;
  return out;
})();

/**
 * Resolve any chain form to its canonical numeric chain ID (string).
 *
 * Accepts: 'ETH' | 'eth' | 'Polygon' | 'MATIC' | '137' | 137 …
 * Returns: '137' (numeric chain-ID string) for known chains.
 * Returns null for: unknown abbrevs, unknown numeric IDs, empty / bad input.
 *
 * A numeric value is accepted ONLY if it is a chain we actually support — an
 * unknown number returns null (fail loud) rather than scanning a chain Alchemy
 * may not have enabled.
 */
export function resolveChainId(input: unknown): string | null {
  if (typeof input !== 'string' && typeof input !== 'number') return null;
  const trimmed = String(input).trim();
  if (trimmed.length === 0) return null;

  // numeric passthrough — but only for chains we know.
  if (/^\d+$/.test(trimmed)) return CHAIN_ID_TO_ABBREV[trimmed] ? trimmed : null;

  const upper = trimmed.toUpperCase();
  return ABBREV_TO_CHAIN_ID[upper] ?? ALIAS_TO_CHAIN_ID[upper] ?? null;
}

/** numeric chain ID → canonical abbrev ('1' → 'ETH'); null if unknown. */
export function chainIdToAbbrev(input: unknown): string | null {
  if (typeof input !== 'string' && typeof input !== 'number') return null;
  return CHAIN_ID_TO_ABBREV[String(input).trim()] ?? null;
}

/** Canonical abbrevs the operator should store (for error messages / UI hints). */
export function listKnownChainAbbrevs(): ReadonlyArray<string> {
  return Object.freeze(Object.keys(ABBREV_TO_CHAIN_ID));
}
